import React from "react";

import {
  Box,
  Heading,
  Image,
  Link,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { ReportFragment } from "../../../graphql/generated/definitions";

import useStore from "../../../store";
import useTranslations from "../../../hooks/useTranslations";
import useDateFnsLocale from "../../../hooks/useDateFnsLocale";
import { dateFormatter } from "../../../hooks/useFormatDate";

import Logo from "../../Logo";

import SectionPrint from "./SectionPrint";
import ReportSignatureBoxes from "./ReportSignatureBoxes";

type ReportPrintProps = { report: ReportFragment };

const ReportPrint: React.FC<ReportPrintProps> = ({ report }) => {
  const t = useTranslations();

  const viewer = useStore((state) => state.viewer);
  const sections = useStore((state) => state.report.sections);

  const template = report.template || report.review.template;

  const locale = useDateFnsLocale();

  return (
    <Box w="full" maxWidth="80%" mx="auto" className="print">
      <Box className="break-after" pt="96">
        <Heading textAlign="center" color="black">
          {template.title}
        </Heading>
        <Box filter="grayscale(1)" pt="6" textAlign="center">
          <Box display="inline-block">
            <Logo width="120px" />
          </Box>
          {viewer.company.visualIdentity && (
            <Box display="inline-block" pl="10">
              <Image
                src={viewer.company.visualIdentity}
                objectFit="cover"
                h="33px"
                alt={viewer.company.name}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box py="16">
        <Heading size="lg" color="black">
          {t("reports.print.generalInformations")}
        </Heading>
      </Box>
      <Box className="break-after">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th color="black"></Th>
              <Th color="black" isNumeric></Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>{t("templates.title")}</Td>
              <Td isNumeric>{template.title}</Td>
            </Tr>

            <Tr>
              <Td verticalAlign="text-top">
                {t("reports.shared.role.supervisor", {
                  count: report.review.reviewSupervisors.length,
                })}
              </Td>
              <Td isNumeric>
                {report.review.reviewSupervisors.map((supervisor) => (
                  <Box key={supervisor.id}>{supervisor.user.name}</Box>
                ))}
              </Td>
            </Tr>

            <Tr>
              <Td verticalAlign="text-top">
                {t("reports.shared.role.collaborator")}
              </Td>
              <Td isNumeric>{report.review.collaborator.name}</Td>
            </Tr>

            <Tr>
              <Td verticalAlign="text-top">
                {t("reports.shared.role.referentPlural", {
                  count: report.reportReferents.length,
                })}
              </Td>
              <Td isNumeric>
                {report.reportReferents.map(({ referent }) => (
                  <Box key={referent.id}>{referent.name}</Box>
                ))}
              </Td>
            </Tr>
            <Tr>
              <Td verticalAlign="text-top">
                {t("reports.print.completionDate")}
              </Td>
              <Td isNumeric>
                <Box>
                  <Box display="inline-block">
                    {report.review.collaborator.name}
                    {" -"}
                  </Box>
                  <Box display="inline-block" pl="1">
                    {report.filledAt
                      ? dateFormatter(
                          report.filledAt,
                          t("dateFormat.long") as string,
                          "",
                          locale
                        )
                      : t("reports.print.uncompleted")}
                  </Box>
                </Box>
                {report.reportReferents.map(({ referent, filledAt }) => (
                  <Box key={referent.id} pt="1">
                    <Box display="inline-block">
                      {referent.name}
                      {" - "}
                    </Box>
                    <Box display="inline-block" pl="1">
                      {filledAt
                        ? dateFormatter(
                            filledAt,
                            t("dateFormat.long") as string,
                            "",
                            locale
                          )
                        : t("reports.print.uncompleted")}
                    </Box>
                  </Box>
                ))}
              </Td>
            </Tr>
            <Tr>
              <Td verticalAlign="text-top">{t("reports.print.questionnaireURL")}</Td>
              <Td isNumeric>
                <Link
                  href={`${window.origin}/reports/${report.id}`}
                  textDecoration="underline"
                >{`${window.origin}/reports/${report.id}`}</Link>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
      <Box className="break-after">
        {sections.map((section) => (
          <SectionPrint key={section.id} section={section} report={report} />
        ))}
      </Box>
      <Box>
        <ReportSignatureBoxes />
      </Box>
    </Box>
  );
};

export default React.memo(ReportPrint);
